import { useState } from "react";
import { useParams, useNavigate, useOutletContext } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { jobProgress, fmtDate } from "@/lib/constants";
import StageTracker from "@/components/StageTracker";
import SubmitUpdateDialog from "@/components/SubmitUpdateDialog";
import ApprovalCard from "@/components/ApprovalCard";
import EditJobDialog from "@/components/EditJobDialog";
import AcceptJobDialog from "@/components/AcceptJobDialog";
import DrawingsSection from "@/components/DrawingsSection";
import RemarksSection from "@/components/RemarksSection";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, Plus, Trash2, Clock, CheckCircle2, FileText, Send, MessageSquare, CalendarClock, CalendarCheck } from "lucide-react";

const ICONS = { created: FileText, submitted: Send, finalised: CheckCircle2, rejected: Clock, edited: FileText, requested: Inbox_, remark: MessageSquare };
function Inbox_(props) { return <FileText {...props} />; }

export default function JobDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useOutletContext();
  const [updateOpen, setUpdateOpen] = useState(false);
  const [initialStage, setInitialStage] = useState(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["job", id], queryFn: async () => (await api.get(`/jobs/${id}`)).data,
  });

  const del = useMutation({
    mutationFn: async () => (await api.delete(`/jobs/${id}`)).data,
    onSuccess: () => {
      toast.success("Job deleted");
      qc.invalidateQueries({ queryKey: ["jobs"] });
      navigate("/jobs");
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  if (isLoading) return <div className="text-muted-foreground" data-testid="job-detail-loading">Loading…</div>;
  if (isError || !data) return <div className="text-destructive">Job not available.</div>;

  const { job, activity, pending_updates, remarks } = data;
  const isManager = user.role === "owner" || user.role === "manager";
  const isCaptain = user.role === "captain";
  const isClient = user.role === "client";
  const isRequested = job.status === "requested";
  const prog = jobProgress(job);
  const stageNames = job.stages.map((s) => s.name);

  const openUpdate = (stage) => { setInitialStage(stage?.name || null); setUpdateOpen(true); };

  return (
    <div className="space-y-6" data-testid="job-detail-page">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="back-button">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div className="rounded-lg border border-border bg-card p-5 sm:p-6 corner-tick">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <span className="font-mono-tag text-sm text-primary" data-testid="detail-job-number">{job.job_number}</span>
            <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">{job.client_name}</h1>
            <p className="text-muted-foreground mt-1 max-w-2xl">{job.ms_description}</p>
            <div className="flex flex-wrap gap-x-6 gap-y-1 mt-3 text-sm">
              <span className="text-muted-foreground">Captain: <span className="text-foreground">{job.assigned_captain_name || "Unassigned"}</span></span>
              <span className="text-muted-foreground">Created: <span className="text-foreground">{fmtDate(job.created_at)}</span></span>
              <span className="text-muted-foreground">Current: <span className="text-foreground">{job.current_stage}</span></span>
              {job.target_completion_date && (
                <span className="text-muted-foreground inline-flex items-center gap-1"><CalendarClock className="h-3.5 w-3.5" /> Target: <span className="text-foreground">{job.target_completion_date}</span></span>
              )}
              {job.actual_completion_date && (
                <span className="text-emerald-400 inline-flex items-center gap-1"><CalendarCheck className="h-3.5 w-3.5" /> Finished: {job.actual_completion_date}</span>
              )}
              {isRequested && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full border border-blue-500/40 bg-blue-500/15 text-blue-400 text-xs font-mono-tag uppercase" data-testid="requested-badge">Request · Pending</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isManager && isRequested && <AcceptJobDialog job={job} />}
            {isManager && <EditJobDialog job={job} />}
            {(isCaptain || isManager) && !isRequested && (
              <Button onClick={() => openUpdate(null)} data-testid="open-submit-update-button">
                <Plus className="h-4 w-4 mr-2" /> Submit Update
              </Button>
            )}
            {user.role === "owner" && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" size="icon" data-testid="delete-job-button"><Trash2 className="h-4 w-4 text-destructive" /></Button>
                </AlertDialogTrigger>
                <AlertDialogContent data-testid="delete-job-dialog">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete this job?</AlertDialogTitle>
                    <AlertDialogDescription>This permanently removes {job.job_number} and all its history.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="delete-cancel">Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={() => del.mutate()} data-testid="delete-confirm" className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </div>
        <div className="mt-4">
          <div className="flex items-center justify-between text-sm mb-1.5">
            <span className="label-eyebrow">Overall Progress</span>
            <span className="font-mono-tag font-semibold">{prog}%</span>
          </div>
          <Progress value={prog} className="h-2" />
        </div>
      </div>

      {!isManager && pending_updates?.length > 0 && isCaptain && (
        <div className="rounded-lg border border-blue-500/30 bg-blue-500/5 p-4" data-testid="captain-pending-notice">
          <p className="text-sm text-blue-400 font-medium">
            You have {pending_updates.length} update(s) pending review. They stay hidden from the client until a Manager or Owner finalises them.
          </p>
        </div>
      )}

      {isManager && pending_updates?.length > 0 && (
        <div>
          <h2 className="font-heading text-xl font-bold mb-3">Pending Approvals for this job</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="detail-pending-updates">
            {pending_updates.map((u) => <ApprovalCard key={u.id} update={u} />)}
          </div>
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading text-xl font-bold">{isClient ? "Live Status" : "Stage Tracker"}</h2>
          {isClient && <span className="text-xs text-muted-foreground">Finalised status only</span>}
        </div>
        <StageTracker
          stages={job.stages}
          simple={isClient}
          onStageAction={((isCaptain || isManager) && !isRequested) ? openUpdate : undefined}
          actionLabel="Submit update for this stage"
        />
      </div>

      <DrawingsSection jobId={id} drawings={job.drawings || []} canUpload={user.role !== "captain"} />

      {!isCaptain && <RemarksSection jobId={id} remarks={remarks || []} currentUserId={user.id} />}

      {!isClient && (
        <div>
          <h2 className="font-heading text-xl font-bold mb-3">Activity Log</h2>
          <div className="rounded-lg border border-border bg-card p-5" data-testid="activity-log">
            {activity.length === 0 && <p className="text-sm text-muted-foreground">No activity yet.</p>}
            <ol className="space-y-4">
              {activity.map((a) => {
                const Icon = ICONS[a.type] || FileText;
                return (
                  <li key={a.id} className="flex gap-3" data-testid={`activity-${a.id}`}>
                    <div className="mt-0.5 h-7 w-7 rounded-full bg-accent flex items-center justify-center shrink-0">
                      <Icon className="h-3.5 w-3.5 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm">{a.message}</p>
                      <p className="text-xs text-muted-foreground font-mono-tag">{fmtDate(a.timestamp)}</p>
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>
        </div>
      )}

      <SubmitUpdateDialog jobId={id} open={updateOpen} onOpenChange={setUpdateOpen} initialStage={initialStage} stages={stageNames} />
    </div>
  );
}
